import {useTheme, alpha} from '@mui/material/styles'
import {Card, CardContent, FormControl, InputLabel, MenuItem, Select, Box} from '@mui/material'
import meterReadingStatus from 'constants/enum/meterReadingStatus'
import dayjs from 'dayjs'
import useResponsive from 'logic/useResponsive'
import React from 'react'
import {CartesianGrid, LineChart, ResponsiveContainer, XAxis, YAxis, Legend, Line} from 'recharts'
import {useGetCurrentUserQuery, useGetMeterReadingDailyQuery} from 'store/Store'
import {groupByField, groupByFieldFunc} from 'utils'
import DashboardChartTooltip from 'components/Page/Dashboard/DashboardChartTooltip'
import {METER_CATEGORY} from 'constants/enum/meterType'
import {useTranslation} from 'react-i18next'
import DayNavigator from 'components/Control/DayNavigator'

const DailyMeterReadings = () => {
    const {t} = useTranslation()
    const [selectedCategory, setSelectedCategory] = React.useState('')
    const [selectedDate, setSelectedDate] = React.useState(dayjs().startOf('day'))

    const {data: dailyMeterReadings} = useGetMeterReadingDailyQuery({
        date: selectedDate.format('YYYY-MM-DD'),
    })
    const {data: userToken} = useGetCurrentUserQuery()
    const {smallScreen} = useResponsive()
    const theme = useTheme()
    const palette = theme.palette
    const lineColors = React.useMemo(
        () => [
            palette.primary.main,
            palette.success.main,
            palette.error.main,
            palette.warning.main,
            palette.info.main,
            palette.secondary.main,
        ],
        [palette]
    )
    const {account} = userToken || {}
    const groupBy = account ? 'structure' : 'account'

    const {chartData, metricKeyList} = React.useMemo(() => {
        if (!dailyMeterReadings) return {chartData: [], metricKeyList: []}
        const keys = new Set()
        const dailyMeterReadingSuccess = dailyMeterReadings
            .filter((reading) => reading.status !== meterReadingStatus.FAILED)
            .filter((reading) => {
                if (!selectedCategory) return true
                const readingCategory = reading?.meter?.category
                return readingCategory === selectedCategory
            })
        const dailyMeterReadingsByGroup = groupByField(dailyMeterReadingSuccess, 'date')
        const chartData = Object.entries(dailyMeterReadingsByGroup).map(([date, readings]) => {
            const readingsGroupBy = groupByFieldFunc(readings, (reading) => reading[groupBy]?.name)
            Object.keys(readingsGroupBy).forEach((key) => {
                keys.add(key)
                const total = readingsGroupBy[key].reduce(
                    (sum, reading) => sum + reading.absolute_value,
                    0
                )
                readingsGroupBy[key] = Number(total.toFixed(2))
            })
            return {
                date: dayjs(date).format('HH:mm'),
                ...readingsGroupBy,
            }
        })
        return {chartData, metricKeyList: Array.from(keys)}
    }, [dailyMeterReadings, groupBy, selectedCategory])

    const domain = React.useMemo(() => {
        if (!chartData.length || !metricKeyList.length) return ['auto', 'auto']
        const values = chartData.flatMap((item) =>
            metricKeyList.map((metric) => Number(item[metric] ?? 0))
        )

        const min = Math.min(...values)
        const max = Math.max(...values)

        if (!isFinite(min) || !isFinite(max)) return ['auto', 'auto']
        if (min === max) {
            const buffer = min === 0 ? 5 : Math.abs(min) * 0.1
            return [min - buffer, max + buffer]
        }

        const padding = (max - min) * 0.08
        return [Number(min - padding).toFixed(2), Number(max + padding).toFixed(2)]
    }, [chartData, metricKeyList])

    // if (!chartData.length) return null

    return (
        <Card
            sx={{
                width: '100%',
                height: '100%',
            }}>
            <CardContent
                sx={{
                    width: '100%',
                    height: 440,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} flexWrap="wrap">
                    <DayNavigator date={selectedDate} onChange={setSelectedDate} />
                    <FormControl size="small" sx={{minWidth: 200}}>
                        <InputLabel id="daily-meter-category-label">
                            {t('filters.category')}
                        </InputLabel>
                        <Select
                            labelId="daily-meter-category-label"
                            label={t('filters.category')}
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}>
                            <MenuItem value="">{t('filters.allCategories')}</MenuItem>
                            {METER_CATEGORY.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {t(`selectOptions.${category.toLowerCase()}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{flex: 1, minHeight: 0}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 30,
                                right: 20,
                                left: 0,
                                bottom: 10,
                            }}>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={alpha(
                                    palette.divider,
                                    theme.palette.mode === 'dark' ? 0.4 : 0.7
                                )}
                            />
                            <XAxis
                                dataKey="date"
                                tick={{
                                    fontSize: 12,
                                    fill: palette.text.secondary,
                                    fontWeight: 500,
                                }}
                                tickMargin={12}
                                axisLine={{stroke: alpha(palette.divider, 0.6)}}
                                tickLine={false}
                            />
                            {!smallScreen && (
                                <YAxis
                                    tick={{
                                        fontSize: 12,
                                        fill: palette.text.secondary,
                                        fontWeight: 500,
                                    }}
                                    domain={domain}
                                    tickMargin={40}
                                    axisLine={false}
                                    tickLine={false}
                                />
                            )}
                            <DashboardChartTooltip
                                formatter={(value, name) => [
                                    `${Number(value ?? 0).toFixed(2)}`,
                                    name,
                                ]}
                            />
                            <Legend
                                verticalAlign="top"
                                align="right"
                                iconType="circle"
                                iconSize={10}
                                wrapperStyle={{
                                    paddingBottom: 16,
                                    fontSize: 12,
                                    color: palette.text.secondary,
                                }}
                            />
                            {metricKeyList.map((key, index) => (
                                <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={lineColors[index % lineColors.length]}
                                    strokeWidth={2.5}
                                    dot={{
                                        r: 0,
                                        strokeWidth: 0,
                                    }}
                                    activeDot={{r: 6}}
                                    connectNulls
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    )
}

export default DailyMeterReadings
